import { Autocomplete, Divider, List, Paper, Popper, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import Icon from '../Icon';
import InputField from '../InputField';

/**
 * @module Components/DesignSystem/CustomAutocomplete
 * @description This module contains the CustomAutocomplete component used across the application.
 */

/**
 * Custom styled Paper component for dropdown.
 * @param {object} props - The props for the Paper component.
 * @param {boolean} props.openUpward - Determines whether the dropdown opens upwards.
 * @returns {JSX.Element} The styled Paper component.
 */
const CustomPaper = styled(Paper)(({ theme, openUpward }) => ({
    backgroundColor: theme.palette.grey[200],
    fontSize: '18px',
    fontFamily: 'DM Sans',
    border: `1px solid ${theme.palette.grey[100]}`,
    borderRadius: openUpward
        ? '10px 10px 0 0' // Top left and right when the list opens upwards
        : '0 0 10px 10px', // Bottom left and right when the list opens downwards
    '& .MuiAutocomplete-noOptions': {
        fontSize: '18px !important',
        fontFamily: 'DM Sans !important',
    },
}));

/**
 * Custom styled List component for dropdown options.
 * @param {object} props - The props for the List component.
 * @returns {JSX.Element} The styled List component.
 */
const CustomList = styled(List)(({ theme }) => ({
    padding: 0,
    '& .MuiAutocomplete-option': {
        fontSize: '18px',
        fontFamily: 'DM Sans',
        color: theme.palette.grey[400],
        borderRadius: 0, // Ensure options have square corners
        '&:hover': {
            color: theme.palette.grey[600], // Hover effect
        },
        '&.Mui-selected': {
            backgroundColor: theme.palette.grey[200], // Selected effect
            color: theme.palette.grey[600], // Text color when selected
        },
    },
}));

/**
 * Custom Popper to detect the placement direction and modify dropdown behavior.
 * @param {object} props - The props for the Popper component.
 * @param {function} [props.onPlacementChange] - A function to handle the placement change.
 * @returns {JSX.Element} The Popper component with updated modifiers.
 */
const CustomPopper = (props) => {
    const { onPlacementChange, ...restProps } = props;

    return (
        <Popper
            {...restProps}
            modifiers={[
                {
                    name: 'flip',
                    options: {
                        altBoundary: true,
                        fallbackPlacements: ['top'],
                    },
                },
                {
                    name: 'onUpdate',
                    enabled: true,
                    phase: 'afterWrite',
                    fn: ({ state }) => {
                        const placement = state.placement;
                        if (onPlacementChange) {
                            onPlacementChange(placement);
                        }
                    },
                },
            ]}
        />
    );
};

/**
 * Custom Autocomplete component based on Material UI with enhanced styling and behavior.
 * 
 * @component
 * @example
 * const options = [{ label: 'Option 1', value: 'option_1 }, { label: 'Option 2', value: 'option_2 }];
 * <CustomAutocomplete
 *    name="example"
 *    value="Option 1"
 *    options={options}
 *    placeholder="Select an option"
 *    handleChange={handleChange}
 *    error={false}
 *    helperText="Please select an option"
 *    onInputChange={handleInputChange}
 * />
 * 
 * @param {object} props - The props for the CustomAutocomplete component.
 * @param {string} props.name - The name attribute for the input field.
 * @param {string|number|null} props.value - The current value of the autocomplete input.
 * @param {Array} [props.options=[]] - The options to display in the dropdown.
 * @param {string} [props.placeholder=''] - The placeholder text for the input field.
 * @param {function} [props.handleChange] - The function to handle value change.
 * @param {boolean} [props.error=false] - Whether the input has an error.
 * @param {string} [props.helperText=''] - The helper text displayed below the input.
 * @param {function} [props.onInputChange] - The function to handle input value change.
 * @param {function} [props.onBlur] - The function to handle input blur event.
 * @param {string} [props.backgroundColor=''] - The background color for the input field.
 * @param {string} [props.height='45px'] - The height of the input field.
 * @returns {JSX.Element} The rendered CustomAutocomplete component.
 */
const CustomAutocomplete = ({
    name,
    value,
    options = [],
    placeholder,
    handleChange,
    error,
    helperText,
    onInputChange,
    onBlur,
    backgroundColor = '',
    height = '45px', // Default height
    ...props
}) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [placement, setPlacement] = useState('bottom');
    const handleBackgroundColor = isEmpty(backgroundColor) ? theme.palette.grey[200] : backgroundColor;

    // Helper function to find the selected option based on value
    const getOptionByValue = (value) => {
        return options.find((option) => option.value === value) || null;
    };

    // Handle blur to ensure Formik validation triggers
    const handleBlur = (event) => {
        if (onBlur) {
            onBlur(event);
        }
    };

    return (
        <Autocomplete
            value={getOptionByValue(value)}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            onChange={(event, newValue) => {
                const newValueToSet = newValue ? newValue.value : null;
                handleChange({ target: { name, value: newValueToSet } });
            }}
            onInputChange={onInputChange}
            options={options}
            onBlur={handleBlur}
            error={error}
            disablePortal
            clearIcon={false}
            PopperComponent={(popperProps) => (
                <CustomPopper
                    {...popperProps}
                    onPlacementChange={setPlacement} // Update placement state
                />
            )}
            getOptionLabel={(option) => option.label || ''}
            renderInput={(params) => (
                <InputField
                    {...params}
                    placeholder={placeholder}
                    variant="outlined"
                    fullWidth
                    onBlur={handleBlur}
                    error={error}
                    helperText={helperText}
                    sx={{
                        '& .MuiInputBase-root': {
                            height,
                            borderRadius:
                                open && placement === 'bottom'
                                    ? '10px 10px 0 0' // Conditional bottom border-radius when list opens downwards
                                    : open && placement === 'top'
                                        ? '0 0 10px 10px' // Conditional top border-radius when list opens upwards
                                        : '10px',
                            backgroundColor: handleBackgroundColor,
                        },
                    }}
                />
            )}
            ListboxComponent={(props) => (
                <CustomList sx={{ backgroundColor: handleBackgroundColor }} {...props}>
                    {React.Children.map(props.children, (child, index) => (
                        <>
                            {React.cloneElement(child, { key: index })}
                            {index < props.children.length - 1 && (
                                <Divider
                                    key={`divider-${index}`}
                                    sx={{ borderStyle: 'dashed', borderColor: theme.palette.grey[100], width: '90%', ml: 1 }}
                                />
                            )}
                        </>
                    ))}
                </CustomList>
            )}
            PaperComponent={(props) => <CustomPaper {...props} openUpward={placement === 'top'} />}
            sx={{
                '& .MuiOutlinedInput-root': {
                    paddingRight: '10px !important',
                },
                '& .MuiAutocomplete-input': {
                    padding: '0px !important',
                    paddingLeft: '8px !important',
                    paddingRight: '0px !important',
                },
                '& .MuiAutocomplete-endAdornment': {
                    // display: 'none',
                    right: '12px !important',
                },
            }}
            popupIcon={<Icon name="down-arrow-icon.png" height={6} width={10} />}
            {...props}
        />
    );
};

/**
 * @module Components/DesignSystem/CustomAutocompleteFreeSolo
 * @description This module contains the CustomAutocompleteFreeSolo component used across the application.
 */
/**
 * CustomAutocompleteFreeSolo component that allows free text input along with suggestions from a list of options.
 *
 * @component
 * @example
 * const options = [{ label: 'Option 1', value: 'option_1 }, { label: 'Option 2', value: 'option_2 }];
 * <CustomAutocompleteFreeSolo
 *    name="example"
 *    value="Option 1"
 *    options={options}
 *    placeholder="Select an option"
 *    handleChange={handleChange}
 *    error={false}
 *    helperText="Please select an option"
 *    onInputChange={handleInputChange}
 * />
 *
 * @param {Object} props The props for the component.
 * @param {string} props.name The name of the input field (for Formik or other form libraries).
 * @param {string} props.value The current value of the input field.
 * @param {Array} [props.options=[]] The list of options available for autocomplete.
 * @param {string} [props.placeholder=''] The placeholder text for the input field.
 * @param {Function} props.handleChange A function to handle the change event when a value is selected or typed.
 * @param {boolean} [props.error=false] A flag to indicate if there's an error.
 * @param {string} [props.helperText=''] A helper text to display when there's an error.
 * @param {Function} [props.onInputChange] A function to handle changes in the input text.
 * @param {Function} [props.onBlur] A function to handle the blur event.
 * @param {string} [props.backgroundColor=''] The background color for the input field.
 * @param {Object} [props.sx] The custom styles passed to the component.
 */
const CustomAutocompleteFreeSolo = ({
    name,
    value,
    options = [],
    placeholder,
    handleChange,
    error,
    helperText,
    onInputChange,
    onBlur,
    backgroundColor = '',
    ...props
}) => {
    const theme = useTheme();
    const [inputValue, setInputValue] = useState('');
    const [open, setOpen] = React.useState(false);
    const hasOptions = options.length > 0;
    const handleBackgroundColor = isEmpty(backgroundColor) ? theme.palette.grey[200] : backgroundColor;

    /**
     * Update internal input value when Formik value changes (e.g., when resetting form).
     */
    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    return (
        <Autocomplete
            freeSolo
            value={value}
            inputValue={inputValue}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            onChange={(event, newValue) => handleChange({ target: { name, value: newValue } })}
            onInputChange={(event, newInputValue) => {
                if (onInputChange) {
                    onInputChange({ target: { name, value: newInputValue } });
                }
            }}
            options={hasOptions ? options : []}
            forcePopupIcon={false}
            clearIcon={false}
            getOptionLabel={(option) => option.label || option || ''}
            renderInput={(params) => (
                <InputField
                    {...params}
                    placeholder={placeholder}
                    variant="outlined"
                    fullWidth
                    onBlur={onBlur}
                    error={error}
                    helperText={helperText}
                    sx={{
                        '& .MuiInputBase-root': {
                            height: '45px',
                            borderRadius: open && hasOptions ? '10px 10px 0 0' : '10px', // Conditional border-radius
                            backgroundColor: handleBackgroundColor,
                        },
                    }}
                />
            )}
            sx={{
                '& .MuiAutocomplete-input': {
                    padding: '0px !important',
                    paddingLeft: '8px !important',
                    backgroundColor: handleBackgroundColor,
                },
            }}
            ListboxComponent={(props) =>
                hasOptions ? (
                    <CustomList sx={{ backgroundColor: handleBackgroundColor }} {...props}>
                        {React.Children.map(props.children, (child, index) => (
                            <>
                                {React.cloneElement(child, { key: index })}
                                {index < props.children.length - 1 && (
                                    <Divider
                                        key={`divider-${index}`}
                                        sx={{
                                            borderStyle: 'dashed',
                                            borderColor: theme.palette.grey[100],
                                            width: '97%',
                                            ml: '1.5%',
                                            mr: '1.5%',
                                        }}
                                    />
                                )}
                            </>
                        ))}
                    </CustomList>
                ) : null
            }
            PaperComponent={(props) => (hasOptions ? <CustomPaper {...props} /> : null)}
            PopperComponent={(props) => (hasOptions ? <CustomPopper {...props} /> : null)}
            {...props}
        />
    );
};

export { CustomAutocomplete, CustomAutocompleteFreeSolo };
